import React, { useState, useEffect, useCallback } from "react";
import "../../Styles/PressTable.css";
import { useNavigate } from "react-router-dom";
import { pressAPI, fileAPI } from "../../api/index"; // adjust path to match your project

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// Backend gives "YYYY-MM-DD" (Pydantic `date`). Display it the way the UI expects.
const formatDisplayDate = (isoDate) => {
  if (!isoDate) return "";
  const [year, month, day] = isoDate.split("-");
  if (!year || !month || !day) return isoDate;
  return `${day}-${month}-${year}`;
};

const PAGE_SIZE = 10;

function PressTable() {
  const navigate = useNavigate();

  const [pressItems, setPressItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [dateFilter, setDateFilter] = useState("All Dates");
  const [status, setStatus] = useState("All Status");
  const [currentPage, setCurrentPage] = useState(1);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editFile, setEditFile] = useState(null);
  const [editPreview, setEditPreview] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // ---------- IMAGE PREVIEW (in-page popup) ----------
  const [previewItem, setPreviewItem] = useState(null);
  const openPreview = (item) => setPreviewItem(item);
  const closePreview = () => setPreviewItem(null);

  // ---------- FETCH ----------
  const fetchPress = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await pressAPI.getAll({});
      const data = Array.isArray(res.data) ? res.data : res.data?.items || [];
      setPressItems(data);
    } catch (err) {
      console.error("Failed to load press items:", err);
      setError(err.response?.data?.detail || "Could not load press photos.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPress();
  }, [fetchPress]);

  // Close image preview with Escape key
  useEffect(() => {
    if (!previewItem) return;
    const handleKey = (e) => {
      if (e.key === "Escape") closePreview();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [previewItem]);

  const resetFilters = () => {
    setSearch("");
    setDateFilter("All Dates");
    setStatus("All Status");
    setCurrentPage(1);
  };

  // ---------- SEARCH + FILTER (client-side) ----------
  const filteredPress = pressItems.filter((item) => {
    const searchLower = search.trim().toLowerCase();
    const matchesSearch =
      searchLower === "" || (item.title || "").toLowerCase().includes(searchLower);

    const matchesStatus =
      status === "All Status" || (item.status || "").toLowerCase() === status.toLowerCase();

    let matchesDate = true;
    if (item.date_added) {
      const [year, month] = item.date_added.split("-").map(Number);
      const today = new Date();
      if (dateFilter === "This Month") {
        matchesDate = month === today.getMonth() + 1 && year === today.getFullYear();
      } else if (dateFilter === "This Year") {
        matchesDate = year === today.getFullYear();
      }
    }

    return matchesSearch && matchesStatus && matchesDate;
  });

  const totalPages = Math.max(1, Math.ceil(filteredPress.length / PAGE_SIZE));
  const pageItems = filteredPress.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  // ---------- EDIT LOGIC ----------
  const openEditModal = (item) => {
    setEditItem({ ...item });
    setEditFile(null);
    setEditPreview(item.photo_url || null);
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditItem(null);
    setEditFile(null);
    setEditPreview(null);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditItem((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditImageChange = (e) => {
    const selected = e.target.files[0];
    if (selected) {
      setEditFile(selected);
      setEditPreview(URL.createObjectURL(selected));
    }
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    setSavingEdit(true);
    try {
      let photoUrl = editItem.photo_url;

      if (editFile) {
        const uploadRes = await fileAPI.upload(editFile, "press");
        photoUrl =
          uploadRes.data?.url ||
          uploadRes.data?.file_url ||
          uploadRes.data?.photo_url ||
          photoUrl;
      }

      await pressAPI.update(editItem.id, {
        title: editItem.title,
        caption: editItem.caption || null,
        status: editItem.status,
        photo_url: photoUrl,
      });

      await fetchPress();
      closeEditModal();
    } catch (err) {
      console.error("Failed to update press item:", err);
      alert(err.response?.data?.detail || "Something went wrong while saving changes.");
    } finally {
      setSavingEdit(false);
    }
  };

  // ---------- DELETE LOGIC ----------
  const openDeleteModal = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await pressAPI.delete(deleteId);
      await fetchPress();
      closeDeleteModal();
    } catch (err) {
      console.error("Failed to delete press item:", err);
      alert(err.response?.data?.detail || "Could not delete this press photo.");
    } finally {
      setDeleting(false);
    }
  };

  // ---------- STATS ----------
  const totalPress = pressItems.length;
  const activeCount = pressItems.filter((p) => p.status === "active").length;

  const recentlyAdded = pressItems.filter((p) => {
    if (!p.date_added) return false;
    const today = new Date();
    const [year, month] = p.date_added.split("-").map(Number);
    return month === today.getMonth() + 1 && year === today.getFullYear();
  }).length;

  return (
    <div className="press123-page">
      {/* Header */}
      <div className="press123-header">
        <div className="press123-header-left">
          <div className="press123-page-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="4" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.8" />
              <path d="M7 8h10M7 12h10M7 16h6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              <path d="M3 20h18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </div>
          <div>
            <h2 className="press123-title">Press</h2>
            <p className="press123-subtitle">
              Manage and organize all your press coverage photos
            </p>
          </div>
        </div>

        <button
          className="press123-add-btn"
          onClick={() => navigate("/dashboard/PressForm")}
        >
          <span className="press123-add-icon">+</span> Add Press Photo
        </button>
      </div>

      {error && <div className="press123-no-data">{error}</div>}

      {/* Stat Cards */}
      <div className="press123-stats-grid">
        <div className="press123-stat-card">
          <div className="press123-stat-icon press123-stat-icon-green">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="4" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.8" />
              <path d="M7 8h10M7 12h10M7 16h6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </div>
          <div className="press123-stat-info">
            <span className="press123-stat-label">Total Press Photos</span>
            <span className="press123-stat-value">{totalPress}</span>
            <span className="press123-stat-sub">All time photos</span>
          </div>
        </div>

        <div className="press123-stat-card">
          <div className="press123-stat-icon press123-stat-icon-orange">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.8" />
              <path d="M3 10h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </div>
          <div className="press123-stat-info">
            <span className="press123-stat-label">Recently Added</span>
            <span className="press123-stat-value">{recentlyAdded}</span>
            <span className="press123-stat-sub">This month</span>
          </div>
        </div>

        <div className="press123-stat-card">
          <div className="press123-stat-icon press123-stat-icon-blue">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
            </svg>
          </div>
          <div className="press123-stat-info">
            <span className="press123-stat-label">Active</span>
            <span className="press123-stat-value">{activeCount}</span>
            <span className="press123-stat-sub">Currently published</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="press123-filters">
        <div className="press123-search-box">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
            <path d="M21 21l-4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <input
            type="text"
            placeholder="Search by press title..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>

        <select
          className="press123-select"
          value={dateFilter}
          onChange={(e) => {
            setDateFilter(e.target.value);
            setCurrentPage(1);
          }}
        >
          <option>All Dates</option>
          <option>This Month</option>
          <option>This Year</option>
        </select>

        <select
          className="press123-select"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setCurrentPage(1);
          }}
        >
          <option>All Status</option>
          <option>Active</option>
          <option>Inactive</option>
        </select>

        <button className="press123-reset-btn" onClick={resetFilters}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
            <path
              d="M4 4v6h6M20 20v-6h-6M4.5 15a8 8 0 0014.5 3.5M19.5 9A8 8 0 005 5.5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Reset Filters
        </button>
      </div>

      {/* Table */}
      <div className="press123-table-wrapper">
        <table className="press123-table table table-hover">
          <thead>
            <tr>
              <th>S.No</th>
              <th>Photo</th>
              <th>Title</th>
              <th>Date Added</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6" className="press123-no-data">Loading...</td>
              </tr>
            ) : pageItems.length === 0 ? (
              <tr>
                <td colSpan="6" className="press123-no-data">
                  No press photos found.
                </td>
              </tr>
            ) : (
              pageItems.map((item, index) => (
                <tr key={item.id}>
                  <td>{(currentPage - 1) * PAGE_SIZE + index + 1}</td>

                  <td>
                    <div className="press123-photo-cell">
                      {item.photo_url ? (
                        <button
                          type="button"
                          className="press123-thumb-btn"
                          onClick={() => openPreview(item)}
                          title="View photo"
                        >
                          <img
                            src={item.photo_url}
                            alt={item.title}
                            className="press123-thumb"
                          />
                        </button>
                      ) : (
                        <span className="press123-no-photo">No photo</span>
                      )}
                    </div>
                  </td>

                  <td>
                    <span className="press123-title-cell">{item.title}</span>
                  </td>

                  <td>
                    <div className="press123-date-cell">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.8" />
                        <path d="M3 10h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                      </svg>
                      {formatDisplayDate(item.date_added)}
                    </div>
                  </td>

                  <td>
                    <span
                      className={`press123-status-badge ${
                        item.status === "active"
                          ? "press123-status-active"
                          : "press123-status-inactive"
                      }`}
                    >
                      {capitalize(item.status)}
                    </span>
                  </td>

                  <td>
                    <button
                      className="press123-edit-btn"
                      onClick={() => openEditModal(item)}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M12 20h9M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4 12.5-12.5z"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      Edit
                    </button>

                    <button
                      className="press123-delete-btn"
                      onClick={() => openDeleteModal(item.id)}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0l-1 14a2 2 0 01-2 2H7a2 2 0 01-2-2L4 6h16z"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="press123-pagination-bar">
        <span className="press123-pagination-info">
          Showing {pageItems.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1} to{" "}
          {(currentPage - 1) * PAGE_SIZE + pageItems.length} of {filteredPress.length} photos
        </span>

        <div className="press123-pagination-controls">
          <button
            className="press123-page-arrow"
            onClick={() => handlePageChange(currentPage - 1)}
          >
            &lt;
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              className={`press123-page-num ${
                currentPage === p ? "press123-active" : ""
              }`}
              onClick={() => handlePageChange(p)}
            >
              {p}
            </button>
          ))}

          <button
            className="press123-page-arrow"
            onClick={() => handlePageChange(currentPage + 1)}
          >
            &gt;
          </button>
        </div>
      </div>

      {/* ---------------- IMAGE PREVIEW POPUP ---------------- */}
      {previewItem && (
        <div className="press123-modal-overlay" onClick={closePreview}>
          <div
            className="press123-modal press123-preview-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="press123-modal-header">
              <h3>{previewItem.title}</h3>
              <button className="press123-modal-close" onClick={closePreview}>
                &times;
              </button>
            </div>

            <div className="press123-preview-body">
              {previewItem.photo_url ? (
                <img
                  src={previewItem.photo_url}
                  alt={previewItem.title}
                  className="press123-preview-img"
                />
              ) : (
                <div className="press123-no-data">No photo available.</div>
              )}
            </div>

            {previewItem.caption && (
              <p className="press123-preview-caption">{previewItem.caption}</p>
            )}
          </div>
        </div>
      )}

      {/* ---------------- EDIT MODAL ---------------- */}
      {showEditModal && editItem && (
        <div className="press123-modal-overlay" onClick={closeEditModal}>
          <div className="press123-modal" onClick={(e) => e.stopPropagation()}>
            <div className="press123-modal-header">
              <h3>Edit Press Photo</h3>
              <button className="press123-modal-close" onClick={closeEditModal}>
                &times;
              </button>
            </div>

            <form className="press123-modal-body" onSubmit={handleEditSave}>
              <div className="press123-form-row">
                <div className="press123-form-group">
                  <label>Press Title</label>
                  <input
                    type="text"
                    name="title"
                    value={editItem.title || ""}
                    onChange={handleEditChange}
                    required
                  />
                </div>

                <div className="press123-form-group">
                  <label>Status</label>
                  <select
                    name="status"
                    value={editItem.status || "active"}
                    onChange={handleEditChange}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <div className="press123-form-group">
                <label>Replace Photo (optional)</label>
                <input type="file" accept="image/*" onChange={handleEditImageChange} />
                {editPreview && (
                  <div className="press123-modal-preview">
                    <img src={editPreview} alt="Preview" style={{ maxWidth: "100%", marginTop: 8 }} />
                  </div>
                )}
              </div>

              <div className="press123-form-group">
                <label>Caption</label>
                <textarea
                  name="caption"
                  rows="3"
                  value={editItem.caption || ""}
                  onChange={handleEditChange}
                />
              </div>

              <div className="press123-modal-footer">
                <button
                  type="button"
                  className="press123-btn-cancel"
                  onClick={closeEditModal}
                  disabled={savingEdit}
                >
                  Cancel
                </button>
                <button type="submit" className="press123-btn-save" disabled={savingEdit}>
                  {savingEdit ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ---------------- DELETE CONFIRM MODAL ---------------- */}
      {showDeleteModal && (
        <div className="press123-modal-overlay" onClick={closeDeleteModal}>
          <div
            className="press123-modal press123-confirm-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="press123-confirm-icon">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                <path
                  d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0l-1 14a2 2 0 01-2 2H7a2 2 0 01-2-2L4 6h16z"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            <h3 className="press123-confirm-title">Delete this photo?</h3>
            <p className="press123-confirm-text">
              This action cannot be undone. Are you sure you want to delete
              this press photo?
            </p>

            <div className="press123-confirm-footer">
              <button
                className="press123-btn-cancel"
                onClick={closeDeleteModal}
                disabled={deleting}
              >
                Cancel
              </button>
              <button className="press123-btn-delete" onClick={confirmDelete} disabled={deleting}>
                {deleting ? "Deleting..." : "OK, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PressTable;